<script setup>
import {useForm} from "../form/useForm";
import {watch, ref, set, reactive, nextTick, computed} from "vue";
import FormShipping from "../form/form-shipping.vue";
import FormWeightDims from "../form/form-weight-dims.vue";
import PrinterSelect from "../layout/printer-select.vue";
import helpers from "@/lib/helpers";
import PackageCartonLabel from "./package-carton-label.vue";
import store from "../../store";
import PackageCustomCarrierLabel from "./package-custom-carrier-label.vue";
import {useDocs} from "@/lib/useDocs";
import PackageDuplicate from "@/components/package/package-duplicate.vue";
import PackageFreightUnit from "@/components/package/package-freight-unit.vue";
import PackageFreightOptions from "@/components/package/package-freight-options.vue";
import {offerCurrent} from "@/lib/freight";

const {printDoc} = useDocs();

const props = defineProps({
  shippable: {
    type: Object,
    required: true
  },
  duplicate: Object,
  value: Object,
  noTransition: Boolean,
  perm: String,
  newmode: String,
})

const emits = defineEmits(['input', 'shipped', 'submit', 'quote'])
const record = ref()
const additional = reactive({
  printer: null,
  ship: false,
  save_printer: false,
  print: false
})
const printer_select = ref()

watch(() => props.value, (v) => {
  record.value = v
}, {
  immediate: true
})

watch(() => additional.ship, (v) => {
  if (props.newmode)
    return

  if (v)
    nextTick(() => {
      const el = document.getElementById('ship_scroll')
      el.scrollTo(0, el.scrollHeight)
    })
})

const {form, changed, save, state, modify, init} = useForm(
    [
      'fkshipping', 'ship_date', 'ship_tracking', 'pkg_length', 'pkg_width', 'pkg_height', 'pkg_weight',
      'pkg_actual_ship_cost', 'notes', 'ship_declared_value', 'reference', 'reference2', 'return_shipment', 'label',
      'ship_accountno', 'bill_zip', 'bill_country', 'signature', 'ship_residential', 'label_type',
      'ship_name', 'ship_company', 'ship_address1', 'ship_address2', 'ship_city', 'ship_state', 'ship_zip', 'ship_country', 'group_packages',
      'alt_tracking', 'handling_type', 'freight_class', 'stackable', 'freight'
    ],
    props.shippable._links.package_url.href,
    record,
    null,
    null,
    {
      force: ['group_packages'],
      defaults: {
        ...(props.shippable.package_init || {}),
        label_type: 'ZPL',
        ...(props.duplicate || {})
      },
    }
)

watch(() => props.newmode, async (mode) => {
  if (changed.length) {
    if (!await helpers.confirm('Discard changes?'))
      return
  }

  if (!props.duplicate)
    init()

  additional.ship = false

  if (mode === 'O') {
    form.return_shipment = false
    // a PO's ship account is for bringing goods in (the vendor's or ours, for the inbound
    // label); a label going out to the vendor must not bill it
    if (props.shippable._links.package_url.href.startsWith('/api/po/')) {
      form.ship_accountno = null
      form.bill_zip = null
      form.bill_country = null
    }
    additional.ship = true
    form.label_type = 'ZPL'
    additional.print = true
  } else if (mode === 'R') {
    form.return_shipment = true
    additional.ship = true
    form.label_type = 'PDF'
    form.signature = false
    form.ship_residential = false
    additional.print = false
  } else if (mode === 'L') {
    form.ship_accountno = null
    form.bill_zip = null
    form.bill_country = null
    form.return_shipment = false
    form.ship_declared_value = null
  } //else
    //form.return_shipment = false
}, {immediate: true})

const shipping = computed(() => {
  return store.state.settings.defaults.shipping.find((s) => s.value == form.fkshipping) || {}
})

watch(shipping, (result) => {
  if (result.nodims) {
    form.pkg_length = 0
    form.pkg_width = 0
    form.pkg_height = 0
    form.group_packages = []
  }
})

/*
 * Freight (LTL): every package is a handling unit - a pallet unless said
 * otherwise - and the shipment's options live on this, the group master.
 * The booking prints a BOL and pallet labels on paper, never a ZPL label.
 */
const isFreight = computed(() => !!shipping.value.freight)

// a copy, so editing the options is a change to save rather than an edit of the record itself
watch(() => form.freight, (v) => {
  if (v && modify.value && v === modify.value.freight)
    form.freight = JSON.parse(JSON.stringify(v))
}, {immediate: true})

// a quote's packages are for quoting freight before the sale: they never ship from it
const isQuote = computed(() => props.shippable._links.package_url.href.startsWith('/api/quote/'))

watch(isFreight, (freight) => {
  if (!freight)
    return
  if (!form.handling_type)
    form.handling_type = 'pallet'
  if (!form.freight)
    form.freight = {}
  ;(form.group_packages || []).forEach(p => p.handling_type || set(p, 'handling_type', 'pallet'))
  form.label_type = 'PDF'
  additional.print = false
}, {immediate: true})

// residential delivery (or pickup, inbound) ticked as the Residential switch says - untick it to override
watch(() => [isFreight.value, form.ship_residential, form.return_shipment], ([freight], old) => {
  if (!freight || !form.freight)
    return
  const acc = {...(form.freight.accessorials || {})}
  const key = form.return_shipment ? 'residential_pickup' : 'residential_delivery'
  const other = form.return_shipment ? 'residential_delivery' : 'residential_pickup'
  // a saved shipment keeps what it says until the switch itself is changed
  if (old && old[0] === freight || !(key in acc)) {
    acc[key] = !!form.ship_residential
    delete acc[other]
    set(form.freight, 'accessorials', acc)
    // and the site type with it: residential, or back to the default when no longer
    if (form.ship_residential)
      set(form.freight, 'location_type', 'residential')
    else if (form.freight.location_type === 'residential')
      set(form.freight, 'location_type', null)
  }
}, {immediate: true})

/*
 * A trucking line is booked only from a current quote. Picked by hand with no
 * offer for it (or one expired), or about to be booked that way, the user is
 * told and the service goes to the freight placeholder (Freight - LTL), which
 * quotes first: nothing is saved or quoted until they go on.
 */
const quotePlaceholder = computed(() => (store.state.settings.defaults.shipping || []).find(s => s.quote_first))
const needsQuote = () => shipping.value.freight_line && !offerCurrent(form.freight && form.freight.offer, shipping.value.scac)

const offerToQuote = async () => {
  const o = form.freight && form.freight.offer
  const expired = o && String(o.scac || '').toUpperCase() === String(shipping.value.scac || '').toUpperCase()
  const name = shipping.value.text
  if (!quotePlaceholder.value) {
    helpers.alert(`${expired ? 'The quote for ' + name + ' has expired' : 'There is no quote for ' + name}: quote these pallets and pick a carrier.`)
    return false
  }
  return await helpers.confirm(
      `${expired ? 'The quote for ' + name + ' has expired' : 'There is no quote for ' + name}, and freight is booked from a quote. `
      + `Change the service to ${quotePlaceholder.value.text} to quote these pallets and pick a carrier?`)
}

const switchToQuote = () => {
  form.fkshipping = quotePlaceholder.value.value
}

/*
 * Our dock's pickup window and notes filled in on outbound freight - a new
 * package, or one just switched to a freight service - so they show and can
 * be changed. Inbound the pickup is at the sender's: what was filled is taken out.
 */
const pickupDefaults = computed(() => (store.state.settings.defaults.freight || {}).pickup || {})
// the form starting over (a new package's kind chosen) clears freight: a freight service gets its options back
watch(() => isFreight.value && !form.freight, (missing) => {
  if (missing)
    form.freight = {}
})

watch(() => [isFreight.value, !!form.return_shipment, !!form.freight], ([freight, inbound], old) => {
  if (!freight || !form.freight || (modify.value && modify.value.shipped))
    return
  const d = pickupDefaults.value
  const pickup = form.freight.pickup || {}

  if (inbound) {
    if (pickup.ready === d.ready && pickup.close === d.close)
      set(form.freight, 'pickup', {ready: null, close: null})
    if (d.instructions && form.freight.pickup_instructions === d.instructions)
      set(form.freight, 'pickup_instructions', null)
    return
  }

  // a saved package opened as it is keeps what it has; new, or freight just chosen, gets ours
  const justFreight = old && (!old[0] || !old[2])
  const turnedOutbound = old && old[1]
  if (modify.value && !justFreight && !turnedOutbound)
    return
  if (!pickup.ready && !pickup.close && d.ready)
    set(form.freight, 'pickup', {ready: d.ready, close: d.close})
  if (!form.freight.pickup_instructions && d.instructions)
    set(form.freight, 'pickup_instructions', d.instructions)
}, {immediate: true})

// a trucking line with no current quote can not be booked: the switch is off and held off
const bookBlocked = computed(() => !!needsQuote())
// (watching the switch too: a new outbound package turns it on as it opens)
watch(() => [bookBlocked.value, additional.ship], ([blocked, ship]) => {
  if (blocked && ship)
    additional.ship = false
}, {immediate: true})

// the Service picked by hand (not from a quote): a trucking line without a current offer is offered the
// quote instead; No keeps the line as picked - freight arranged another way, entered by hand (not booked here)
const service_changed = async () => {
  if (needsQuote() && await offerToQuote())
    switchToQuote()
}

/** a quote chosen from the results: its line, and on a freight line the offer to book */
const choose_quote = (ship) => {
  form.fkshipping = ship.ship_id
  if (ship.freight_offer)
    form.freight = {...(form.freight || {}), offer: ship.freight_offer}
}

const add_slave = () => {
  if (!Array.isArray(form.group_packages))
    set(form, 'group_packages', [])

  form.group_packages.push(isFreight.value ? {handling_type: 'pallet', freight_class: form.freight_class} : {})
  document.getElementById('ship_scroll').scrollTop = document.getElementById('ship_scroll').scrollTop + 150
}

const delete_slave = (slave) => {
  form.group_packages = form.group_packages.filter(s => s !== slave)
  _save()
}

const carrier_label_preview = (url) => {
  window.open(url, '_blank')
}

/** what OMS read off a customer label just uploaded: say so, or what is left to fill in */
const labelScanToast = (scan) => {
  if (!scan) return
  if (scan.error)
    return helpers.toast('Label saved, but it could not be read. Set the ship service and tracking.', false, 'Customer label')
  const parts = [scan.read ? `Read ${scan.read}` : 'No carrier or service found']
  parts.push(scan.tracking ? `tracking ${scan.tracking}` : 'no tracking number found')
  parts.push(scan.shipping ? `service set to ${scan.shipping}` : 'no matching ship service: choose one')
  helpers.toast(parts.join(', ') + '.', false, 'Customer label')
}

// a freight placeholder (Freight - LTL): booking it means quoting first - the
// carrier picked from the quote is then booked (book(), from the packages modal)
const quoteFirst = computed(() => !!shipping.value.quote_first)

const _save = async (confirmed = false) => {
  // about to book a trucking line with no current quote: quote instead
  if (additional.ship && !confirmed && needsQuote()) {
    if (await offerToQuote())
      switchToQuote()
    return
  }

  // booking freight schedules a real pickup: say what is about to be booked
  if (additional.ship && isFreight.value && !quoteFirst.value && !confirmed) {
    const o = (form.freight && form.freight.offer) || {}
    const what = o.carrier_name ? `${o.carrier_name}${o.service_name ? ' - ' + o.service_name : ''}${o.total ? ' for $' + Number(o.total).toFixed(2) : ''}` : shipping.value.text
    if (!await helpers.confirm(`Book ${what}? This schedules the pickup with the carrier.`))
      return
  }

  const updated = await save()
  labelScanToast(updated && updated.label_scan)

  if (additional.ship && quoteFirst.value) {
    // not saved (a field refused): nothing to quote yet
    if (!updated)
      return
    emits('input', updated)
    emits('quote', {book: true})
    return
  }

  if (additional.ship) {
    try {
      const result = await helpers.asyncAction(updated._links.self_url.href, 'PUT', {
        data: {
          task: 'ship',
        }
      })
      helpers.toast(isFreight.value ? 'Freight booked' : 'Shipment shipped')

      if (additional.print && updated.label_type === 'ZPL') {
        printDoc(additional.printer, 'LabelCarrier', result._links.carrier_label_url, 1, false, {
          printer_type: 'printers.carrier',
          save_printer: additional.save_printer,
        }).then(() => {
          if (additional.save_printer)
            printer_select.value.loadSettings()
        })
      }
      emits('input', result)
    } catch {
      emits('input', await helpers.asyncAction(updated._links.self_url.href, 'GET'))
    }
  } else
    emits('input', updated)

}

const return_state = computed(() => {
  if (!form.return_shipment && form.return_shipment !== false)
    return false
  else if (state.value.return_shipment)
    return false

  return null
})

const delete_pkg = async () => {
  if (!await helpers.confirm('Delete this shipment?'))
    return

  await helpers.asyncAction(modify.value._links.delete_url)

  helpers.toast('Shipment deleted')

  emits('input', null)
}

const run_quote = async () => {
  if (changed.value.length > 0) {
    if (!await helpers.confirm('Shipment has changed, save to quote?'))
      return
    // quoted only once saved: a service just changed to freight is otherwise quoted as the parcel it was
    const updated = await save()
    if (!updated)
      return
    emits('input', updated)
  }
  emits('quote')
}

/** book what is now on the form: the line just picked from a quote */
const book = async (confirmed = false) => {
  additional.ship = true
  form.label_type = 'PDF'
  await _save(confirmed)
}

defineExpose({save, form_set: (f, v) => form[f] = v, choose_quote, book})

</script>

<template>
  <div>
    <modal-portal to="footer">
      <div class="package-footer">
        <b-button @click="run_quote" v-if="!!modify" size="sm" variant="outline-secondary">Quote</b-button>

        <b-button @click="delete_pkg" v-if="can(perm + '.packages.delete') && !!modify" size="sm"
                  variant="outline-danger">Delete
        </b-button>

        <package-duplicate :perm="perm" v-if="!!modify" @copy="(v) => emits('copy', v)"/>

        <b-button @click="_save()" size="sm" variant="outline-primary">
          <template v-if="additional.ship && quoteFirst">Save, Quote, &amp; Book</template>
          <template v-else>Save<template v-if="additional.ship"> + {{ isFreight ? 'Book' : 'Ship' }}</template></template>
        </b-button>
      </div>
    </modal-portal>

    <template v-if="!!modify">
      <b-alert variant="danger" v-if="modify.last_ship_error" show class="d-flex">
        <div class="flex-fill my-auto">{{ modify.last_ship_error }}</div>
        <div v-if="can(perm + '.packages.api-data') && (modify._links.request_data_url || modify._links.response_data_url)">
          <b-dropdown variant="link" no-caret size="sm">
            <template #button-content>
              <font-awesome-icon size="lg" class="text-secondary" icon="fa-solid fa-ellipsis-v"/>
            </template>
            <b-dropdown-item-button v-if="modify._links.request_data_url"
                @click="emits('submit'); $router.push({name: 'data', params: {url: modify._links.request_data_url}})">
              API
              Request Data
            </b-dropdown-item-button>
            <b-dropdown-item-button v-if="modify._links.response_data_url"
                @click="emits('submit'); $router.push({name: 'data', params: {url: modify._links.response_data_url}})">
              API
              Response Data
            </b-dropdown-item-button>
          </b-dropdown>
        </div>
      </b-alert>
    </template>

    <b-alert class="mt-3" color="primary" v-if="newmode" show>Creating {{
        {
          O: 'an Outbound',
          R: 'a Return/Inbound',
          L: 'a Prepaid/Customer'
        }[newmode]
      }} Shipment
    </b-alert>
    <form-card title="General">
      <template v-if="!newmode">
        <modal-row label="Return Shipment" :feedback="state.return_shipment">
          <b-form-select
              v-model="form.return_shipment"
              :options="[{value: false, text: 'NO'}, {value: true, text: 'YES'}]"
              :state="return_state"/>
        </modal-row>
      </template>

      <modal-row label="Service" :feedback="state.fkshipping">
        <form-shipping v-model="form.fkshipping" with-null :state="state.fkshipping | state" @change="service_changed"/>
      </modal-row>

      <template v-if="newmode !== 'L'">
        <form-divider/>

        <modal-row label="Declared Value" :feedback="state.ship_declared_value">
          <b-input-group size="sm" prepend="$">
            <form-currency v-model="form.ship_declared_value" :state="state.ship_declared_value | state"/>
          </b-input-group>
        </modal-row>

        <modal-row label="References" :feedback="[state.reference, state.reference2]">
          <b-input-group size="sm">
            <b-form-input placeholder="Ref 1" size="sm" :state="state.reference | state" v-model="form.reference"/>
            <b-form-input placeholder="Ref 2" size="sm" :state="state.reference2 | state" v-model="form.reference2"/>
          </b-input-group>
        </modal-row>

        <modal-row label="Bill Account" :feedback="[state.ship_accountno, state.bill_zip, state.bill_country]">
          <b-input-group size="sm">
            <b-form-input placeholder="Account #" v-model="form.ship_accountno"
                          :state="state.ship_accountno | state"/>
            <b-form-input placeholder="Bill Zip" v-model="form.bill_zip" :state="state.bill_zip | state"/>
            <b-form-input placeholder="Bill Country" v-model="form.bill_country" :state="state.bill_country | state"/>
          </b-input-group>
        </modal-row>

        <modal-row label="Options">
          <b-form-checkbox switch class="my-2" v-model="form.signature" :state="state.signature | state">Signature
          </b-form-checkbox>
          <b-form-checkbox switch class="my-2" v-model="form.ship_residential"
                           :state="state.ship_residential | state">
            Residential
          </b-form-checkbox>
        </modal-row>
      </template>
    </form-card>

    <form-card title="Master Package">
      <template v-if="newmode !== 'L'">
        <form-weight-dims v-model="form" :state="state" :nodims="!!shipping.nodims"/>
        <package-freight-unit v-if="isFreight" class="pt-3" :value="form" :state="state"/>

        <b-alert show v-if="!!shipping.warning" class="mt-3 mb-0" variant="warning" v-text="shipping.warning"/>
      </template>

      <div :class="{'pt-3': newmode !== 'L'}" v-if="newmode !== 'R' && newmode !== 'O' && !isQuote">
        <!-- a customer label: the tracking is read off it on upload, and can be corrected here -->
        <modal-row label="Tracking" :feedback="state.ship_tracking" v-if="newmode === 'L'">
          <b-input v-model="form.ship_tracking" :state="state.ship_tracking | state" placeholder="read from the label when uploaded"/>
        </modal-row>
        <template v-if="newmode !== 'L'">
          <form-row label="Ship Date" :feedback="state.ship_date">
            <b-form-datepicker v-model="form.ship_date" :state="state.ship_date | state" size="sm"/>
          </form-row>
          <modal-row :label="isFreight ? 'PRO #' : 'Tracking'" :feedback="state.ship_tracking">
            <b-input v-model="form.ship_tracking" :state="state.ship_tracking | state"/>
          </modal-row>
          <modal-row label="BOL #" :feedback="state.alt_tracking" v-if="isFreight">
            <b-input v-model="form.alt_tracking" :state="state.alt_tracking | state"/>
          </modal-row>
          <modal-row label="Ship Cost" :feedback="state.pkg_actual_ship_cost">
            <b-input-group size="sm" prepend="$">
              <form-currency :state="state.pkg_actual_ship_cost | state" v-model="form.pkg_actual_ship_cost"/>
            </b-input-group>
          </modal-row>
          <modal-row label="Carton Label" v-if="!!modify">
            <package-carton-label :url="modify._links.label_url"/>
          </modal-row>
        </template>
        <modal-row label="Label">
          <package-custom-carrier-label :package="modify" @input="(v) => { form.label = v;  modify && _save(); }"/>
        </modal-row>
      </div>
    </form-card>

    <form-card title="Freight" v-if="isFreight && form.freight">
      <package-freight-options :value="form.freight" :inbound="!!form.return_shipment"/>
    </form-card>

    <form-card :title="isFreight ? 'Additional Pallets' : 'Additional Packages'" v-if="!shipping.nodims">
      <div v-if="Array.isArray(form.group_packages)" v-for="(slave,index) in form.group_packages" :key="index"
           class="mb-1 mt-1">
        <form-divider v-if="index > 0"/>
        <div class="d-flex">
          <div>
            <form-weight-dims autofocus v-model="form.group_packages[index]" class="flex-fill" v-if="newmode !== 'L'"/>
            <package-freight-unit v-if="isFreight" class="pt-3" :value="form.group_packages[index]"/>
            <modal-row label="Declared Value" class="pt-3">
              <form-currency v-model="form.group_packages[index].ship_declared_value" />
            </modal-row>

            <div :class="{'pt-3': newmode !== 'L'}" v-if="newmode !== 'R' && newmode !== 'O' && !isQuote">
              <template v-if="newmode !== 'L'">
                <modal-row :label="isFreight ? 'PRO #' : 'Tracking'">
                  <b-input v-model="form.group_packages[index].ship_tracking"/>
                </modal-row>
                <modal-row label="BOL #" v-if="isFreight">
                  <b-input v-model="form.group_packages[index].alt_tracking"/>
                </modal-row>
                <modal-row label="Ship Cost">
                  <b-input-group size="sm" prepend="$">
                    <form-currency v-model="form.group_packages[index].pkg_actual_ship_cost"/>
                  </b-input-group>
                </modal-row>
                <form-row label="Ship Date">
                  <b-form-datepicker v-model="form.group_packages[index].ship_date" size="sm"/>
                </form-row>
              </template>

              <modal-row label="Label">
                <package-custom-carrier-label
                    :package="modify && modify.group_packages[index].id && modify.group_packages[index]"
                    @input="(v) => {  form.group_packages[index].label = v;  modify && _save(); }"
                />
              </modal-row>
            </div>
          </div>
          <div class="ml-auto px-3 my-auto">
            <b-btn @click="delete_slave(slave)" variant="danger">
              <font-awesome-icon :icon="['far', 'trash-can']"/>
            </b-btn>
          </div>
        </div>
      </div>

      <b-btn variant="link" block @click="add_slave" size="sm" class="mt-3">{{ isFreight ? 'Add Pallet' : 'Add Package' }}</b-btn>
    </form-card>

    <form-card title="Notes">
      <b-textarea rows="5" v-model="form.notes"/>
    </form-card>

    <form-card title="Shipping" v-if="newmode !== 'L' && !isQuote">
      <modal-row :label="isFreight ? 'Book Freight' : 'Generate Label'">
        <b-check v-model="additional.ship" switch :disabled="bookBlocked"></b-check>
        <small class="text-muted" v-if="bookBlocked">No current quote for this carrier: Quote and pick one to book</small>
      </modal-row>
      <b-alert show variant="info" class="small mb-0" v-if="additional.ship && quoteFirst">
        Quotes these pallets first; the carrier you pick is booked with ShipStation, and the BOL and pallet
        labels saved as this shipment's label. The PRO # comes from the carrier after pickup.
      </b-alert>
      <b-alert show variant="info" class="small mb-0" v-else-if="additional.ship && isFreight">
        Books the chosen carrier with ShipStation (quoted again if the offer has expired) and saves the BOL and
        pallet labels as this shipment's label. The PRO # comes from the carrier after pickup.
      </b-alert>
      <template v-if="additional.ship && !isFreight">
        <modal-row label="Label Type" :feedback="state.label_type">
          <b-form-radio-group :state="state.label_type | state"
                              :options="$store.state.settings.defaults.ship_label_type" v-model="form.label_type"/>
        </modal-row>

        <modal-row label="Print" v-if="form.label_type === 'ZPL'">
          <b-check switch v-model="additional.print" class="mt-2">Print Label</b-check>
          <div v-show="additional.print">
            <printer-select v-model="additional.printer" type="LabelCarrier" setting="printers.carrier"
                            ref="printer_select"/>
            <b-check switch v-model="additional.save_printer" class="my-2">Save As Default Printer</b-check>
          </div>
        </modal-row>
      </template>
    </form-card>

    <!--<div style="height: 50px"/>-->

  </div>
</template>

<style scoped>
/* Footer buttons stay in one right-aligned row, wrapping as a group (not one stray button) on a phone */
.package-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: .5rem;
}

.package-footer > .btn {
  padding-left: 1rem;
  padding-right: 1rem;
}

@media (max-width: 575.98px) {
  .package-footer > .btn {
    padding-left: .5rem;
    padding-right: .5rem;
  }
}

</style>